/* eslint-disable no-await-in-loop */
import Socket from './socket';
import IDB from './iDB';
import Dexie from 'dexie';
import groupBy from 'lodash/groupBy';
import maxBy from 'lodash/maxBy';
import mergeWith from 'lodash/mergeWith';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import debounce from 'lodash/debounce';
import { QueueJob, ThrottleData } from './util';

const queueSymbol = Symbol('queue');

const parseData = value => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }
  return value;
};

export default class Sync {

  constructor({ token, userId, wsUrl, language, dbName, storeConfig, appVersion, queueOptions, projectList }, syncEvent){
    this.userId = userId;
    this.appVersion = appVersion;
    this.language = language;
    this.projectIds = [];
    this.projectList = projectList;
    this.isSyncEnd = false;
    this.dbName = dbName;
    this.storeConfig = storeConfig;
    this.token = token;
    this.wsUrl = wsUrl;
    this.syncEvent = syncEvent;
    this.debounceSetStoreProjectList = debounce(this.setStoreProjectList, 200);
    this[queueSymbol] = new QueueJob(queueOptions || {});
    this.init();
  }

  async init() {
    await this.initDB();
    this.initThrottleData();
    this.handleProjectListChange(this.projectList);
    this.initWebsocket({ token: this.token, language: this.language, wsUrl: this.wsUrl });
  }

  destory() {
    this.socket?.socket?.destroy();
    if (this[queueSymbol]) {
      this[queueSymbol].destroy();
    }
  }

  get eventMap() {
    return {
      'global:sync': this.handleSync.bind(this),
      'global:re-sync': this.handleReSync.bind(this),
      'global:projectUser': this.projectUserChange.bind(this),
      'global:connect': this.handleConnect.bind(this),
      'global:disconnect': this.handleDisConnect.bind(this),
      'global:version-ack': this.handleVersionAck.bind(this),
      'global:projectQueryAll': this.handleProjectQueryAll.bind(this),
      'global:incremental-sync-done': this.handleSyncEnd.bind(this)
    };
  }

  /**
   * 监听同步数据
   * @param {*} e
   */
  handleSync(e){
    this.throttleSyncData(e);
  }

  /**
   * 监听补偿数据
   * @param {*} e
   */
  handleReSync(e){
    this.throttleSyncData({ ...e, reSync: true, needAck: true });
  }

  /**
 * 连接成功
 * @param {*} e
 */
  handleConnect(){
    console.log('/indexedDB连接成功,准备检查版本信息');
    this.checkVersion();
  }

  handleDisConnect(){
    console.log('断开链接');
    this.isSyncEnd = false;
    this.syncEvent.setSyncEnd(false);
  }

  /**
   * 监听当前用户被项目移除
   * @param {*} e
   */
  projectUserChange({ data, action }){
    action === 'D' && this.handleDeleteProject(data);
    action === 'A' && this.handleAddProject(data);
  }

  /**
   * 角色权限变更
   * @param {*} e
   */
  async handleProjectQueryAll(){
    const projectList = await this.syncEvent.loadProjects({ showArchived: '1' });
    await this.handleProjectListChange(projectList || []);
    this.readySyncData();
  }

  /**
   * 同步结束
   * @param {*} e
   */
  handleSyncEnd(){
    this.isSyncEnd = true;
    this.syncEvent.setSyncEnd(true);
  }

  /**
   * 新增项目(重新发送pull-sync)
   *
   */
  async handleAddProject(data) {
    if (!data.id){
      return;
    }
    if (this.projectIds.includes(data.id)){
      return;
    }
    this.projectIds.push(data.id);
    this.readySyncData();
  }

  /**
   * 删除项目
   * 1.删除indexedDB被删除的项目数据，
   * 2.将删除的项目同步id清除
   * 3.重新发送pull-sync
   */
  async handleDeleteProject(data) {
    if (!data.id){
      return;
    }
    const index = this.projectIds.indexOf(data.id);
    if (index === -1){
      return;
    }
    this.projectIds.splice(index, 1);
    // 删除数据
    this.deleteProjectData(data.id);
    // 重置Meta表sync
    const syncMeta = await this.db.table('meta').get('sync');
    let lastProjectSync = syncMeta?.lastProjectSync || {};
    lastProjectSync = omit(lastProjectSync, [data.id]);
    await this.db.table('meta').put({
      ...syncMeta,
      lastProjectSync,
      updateAt: Date.now()
    });
    await this.db.table('project').where({ id: data.id }).delete();
    await this.readySyncData();
  }

  initWebsocket({ token, language, wsUrl }){
    this.socket = new Socket(this.eventMap);
    this.socket.connectSocket({ token, language, wsUrl });
  }

  async initDB(){
    this.db = new IDB(this.dbName, this.storeConfig, { clearOfRebuild: true, maxUpdateTime: Infinity });
    const language = await this.db.meta.get('language');
    if (!language || language.locale !== this.language) {
      await this.rebuild();
      this.db.table('meta').put({ type: 'language', locale: this.language });
    }
  }

  initThrottleData(){
    this.throttleData = new ThrottleData(this.processSync.bind(this), { wait: 100 });
    this.reThrottleData = new ThrottleData(this.processSync.bind(this), { wait: 100 });
  }

  getProjectTables (tables) {
    return tables.filter(table => table.schema.indexes.some(n => n.keyPath === 'projectId'));
  }

  async handleProjectListChange(projectList){
    this.projectIds = projectList.map(item => item.id);
    const curIds = await this.db.table('project').toCollection().primaryKeys();
    this.debounceSetStoreProjectList(projectList);
    if (projectList.length) {
      await this.db
        .table('project')
        .bulkPut(projectList.map(p => ({ ...p, projectId: p.id })));
    }
    const delIds = curIds.filter(id => !this.projectIds.includes(id));
    if (delIds?.length) {
      await this.updateLastSync(delIds);
      Promise.all(this.getProjectTables(this.db.tables).map(table => table.where('projectId').anyOf(delIds).delete()));
    }
  }

  async updateLastSync(delIds) {
    if (!delIds.length) {
      return;
    }
    const syncMeta = await this.db.table('meta').get('sync');
    let lastProjectSync = syncMeta?.lastProjectSync || {};
    lastProjectSync = omit(lastProjectSync, delIds);
    await this.db.table('meta').put({
      ...syncMeta,
      lastProjectSync,
      updateAt: Date.now()
    });
  }

  /**
   * 监听版本信息反馈
   * @param {*} e
   */
  async handleVersionAck(e){
    if (isNil(e?.op)){
      return;
    }
    // 数据版本不一致
    if ([1, 3].includes(e.op)){
      await this.rebuild();
      await this.db.table('meta').put({ type: 'version', dataVersion: e.dataVersion });
      this.checkVersion();
      return;
    }
    if ([2, 3].includes(e.op)){
      console.log('请刷新浏览器');
      this.syncEvent.showNotification(
        {
          duration: 0,
          title: '警告⚠️',
          position: 'bottom-right',
          dangerouslyUseHTMLString: true,
          message: '系统已升级，请<a style="color: #4477e6;" href="javascript: window.location.reload();"> 刷新 </a>浏览器重新加载'
        }
      );
      return;
    }
    this.readySyncData();
  }

  /**
   * 语言变更
   * @param {*} language
   */
  async handleLanguageChange(language){
    if (this.language === language){
      return;
    }
    this.language = language;
    await this.rebuild();
    this.db.table('meta').put({ type: 'language', locale: language });
    this.checkVersion();
  }

  async readySyncData() {
    const syncId = await this.db.getLastSyncId();
    const indexDbId = await this.db.getId();
    const lastProjectSync = (await this.db?.getlastProjectSync()) || {};
    const projectIds = [0, ...this.projectIds];
    const projectSyncs = projectIds.map(id => ({
      projectId: id,
      syncId: lastProjectSync[id] || 0
    }));
    this.socket.socket.emit('pull-sync', {
      syncId,
      indexDbId,
      language: this.language,
      projectSyncs
    });
  }

  async rebuild(rebuild) {
    await this.db.clear({ clearId: false, clearSync: true });
    return rebuild && rebuild();
  }

  /**
 * 处理补偿数据反馈
 * @param {*} data 原数据
 * @param {*} isSuccess 是否处理成功
 */
  async resyncAck(data, isSuccess){
    const indexedDBId = await this.db.getId();
    const syncId = data.syncId;
    this.socket.socket.emit('resync-ack', { data, syncId, indexedDBId, success: isSuccess });
  }

  getThrottleData(e) {
    return this[e.reSync ? 'reThrottleData' : 'throttleData'];
  }

  /**
   * 同步数据
   * @param {*} e
   * @returns
   */
  async throttleSyncData(e) {
    if (!this.db) {
      return;
    }
    if (Array.isArray(e)) {
      e.forEach(item => {
        const itemData = this.processThrottleSyncData(item, this.db?.lastSyncId);
        const throttleData = this.getThrottleData(itemData);
        throttleData.put(itemData);
      });
    } else {
      const throttleData = this.getThrottleData(e);
      throttleData.put(this.processThrottleSyncData(e, this.db?.lastSyncId));
    }
  }

  processThrottleSyncData (e, lastSyncId) {
    let data = parseData(e.data) || {};
    if (e.model === 'project') {
      this.handleProjectSync(e);
    }
    if (e.model === 'issue') {
      const { data: realData } = data;
      Reflect.deleteProperty(data, 'data');
      data = { ...data, ...realData };
    }
    return {
      ...e,
      id: e.syncId || lastSyncId || 0,
      data
    };
  }

  setStoreProjectList(newProjectList){
    this.syncEvent.setStoreProjectList(newProjectList);
  }

  async handleProjectSync(e){
    const projecList = await this.syncEvent.getStoreProjectList();
    const newProjectList = this.getNewProjectList(e, projecList);
    this.debounceSetStoreProjectList(newProjectList);
  }

  async checkVersion(){
    const indexedDBId = await this.db?.getId();
    const versionMeta = await this.db.meta.get('version');
    const dataVersion = versionMeta?.dataVersion || 0;
    this.socket.socket.emit('check-version', {
      version:`${this.appVersion}.${dataVersion}`,
      indexedDBId
    });
  }

  /**
 *
 * @param {*} e 变更动作
 * @param {*} originDatas 变更前数据
 * @param {*} key 数据主键
 * @returns 变更动作后的数据
 */
  getNewProjectList(e, originDatas = [], key = 'id'){
    if (e.action === 'D') {
      return originDatas.filter(i => {
        return i[key] !== e.data[key];
      });
    }
    const index = originDatas.findIndex(i => i[key] === e.data[key]);
    if (index > -1) {
      originDatas[index] = Object.assign(originDatas[index], e.data);
    } else {
      originDatas.push(e.data);
    }
    return originDatas;
  }

  async processSync(data, option = {}){
    if (!this.db) {
      return;
    }
    const isReSync = data[0]?.reSync;
    option = {
      compare: isReSync ? true : false,
      checkId: isReSync ? false : true,
      ...option
    };
    try {
      await this.applyDelta(data, {
        cb: (lastSyncId, successData)=>{
          if (this.isSyncEnd) {
            this.syncEvent.subject(successData);
          }
          // 同步错误数据重新resync一次
          if (!isReSync && successData.length !== data.length){
            let errorData = data.filter(item =>!successData.some(
              successItem => successItem.bizId === item.bizId && successItem.model === item.model
            ));
            errorData = errorData.map(item => ({ ...item, reSync: true }));
            this.processSync(errorData, { compare: true, checkId: false });
          }
          if (successData.length){
            successData.filter(item => item.needAck).forEach(
              item =>{
                this.resyncAck(item, true);
              }
            );
          }
        },
        ...option
      });
    } catch (e) {
      const error = e?.error || e;
      if (error && error instanceof Dexie.DexieError) {
        if (error instanceof Dexie.InvalidTableError) {
          const table = error.message?.match(/Table (.*) does not exist/i);
          if (table && this.db[table]) {
            this.initDB();
          }
        }
        if (error instanceof Dexie.InvalidStateError || error instanceof Dexie.DatabaseClosedError) {
          this.initDB();
        }
      }
      // 一批出现异常处理
      if (data.length > 1){
        const mid = Math.floor((data.length) / 2);
        this.processSync(data.slice(0, mid), { compare: true, checkId: false });
        this.processSync(data.slice(mid), { compare: true, checkId: false });
        return;
      }
      if (data[0].needAck){
        this.resyncAck(data[0], false);
      } else {
        console.error('process sync error', { error: e?.error || e, data });
        this.syncEvent.handleError(error, data);
        this.socket.socket.emit('sync-error', { data, message: error?.message, stack: error?.stack?.substring(0, 300) });
      }
    }
  }

  pushQueue(fn) {
    this[queueSymbol]?.push(fn);
    return this;
  }

  async applyDelta(data, { wait = true, cb, compare = false, checkId = true, compareProp = 'updateTime' } = {}) {
    return new Promise((resolve, reject) => {
      this.pushQueue(async () => {
        try {
          if (checkId) {
            const lastSyncId = await this.db.getLastSyncId();
            data = data.filter(item => item.id > lastSyncId);
          }
          await this.doTransactionData(data, {
            wait,
            compare,
            compareProp,
            cb: async () => {
              let lastSyncId = maxBy(data, o => o?.id || 0)?.id || 0;
              const dbLastSyncId = await this.db.getLastSyncId();
              lastSyncId = Math.max(lastSyncId, dbLastSyncId);
              //存储每个项目最大同步id
              const dblastProjectSync = await this.db.getlastProjectSync();
              const dataGroupByproject = groupBy(data, 'projectId');
              const lastProjectSync = mergeWith(dblastProjectSync, dataGroupByproject, (objValue, srcValue) =>{
                return Math.max(maxBy(srcValue, o => o.id)?.id || 0, objValue || 0);
              });
              await this.db.table('meta').put({ type: 'sync', lastSyncId, lastProjectSync, updateAt: Date.now() });
              if (typeof cb === 'function') {
                cb(lastSyncId, data);
              }
              resolve(lastSyncId);
            }
          });
        } catch (e) {
          reject({ error: e, data });
        }
      });
    });
  }

  async doTransactionData(data, { wait, cb, compare = false, compareProp = 'updateTime' } = {}) {
    const modelData = groupBy(data, 'model');
    compareProp = compareProp || 'updateTime';
    return this.db.transaction('rw', this.db.tables, async () => {
      for (const key of Object.keys(modelData)) {
        const tb = this.db.table(key);
        const list = modelData[key];
        const actionData = groupBy(list, 'action');
        const putData = [];
        const waitList = [];
        if (actionData.A?.length) {
          putData.push(...actionData.A);
        }
        if (actionData.U?.length) {
          // 修改的数据需要判断当前数据是否比旧数据新
          if (compare) {
            const ids = actionData.U.map(n => n.bizId);
            const oldList = await tb.bulkGet(ids);
            const uList = actionData.U.filter((n, i) => {
              const old = oldList[i];
              if (!old) {
                return true;
              }
              return !old[compareProp] || !n?.data?.[compareProp] || n?.data?.[compareProp] >= old[compareProp];
            });
            putData.push(...uList);
          } else {
            putData.push(...actionData.U);
          }
        }
        if (putData.length) {
          waitList.push(tb.bulkPut(putData.map(n => n.data)));
        }
        if (actionData.D?.length) {
          waitList.push(tb.bulkDelete(actionData.D.map(n => n.bizId)));
        }
        if (wait) {
          await Promise.all(waitList);
        }
      }
      if (typeof cb === 'function') {
        await cb();
      }
    });
  }

}
